// @ts-nocheck
import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useLocalization, type Language } from '../../../../config/localization';
import { isOutgoingImagePolicyValid } from '../../../../utils/outgoingImagePolicy';
import Icon, { ICONS } from '../../../ui/icons';
import WorkflowModalSidebar, { type SectionId, type SectionStatus } from './WorkflowModalSidebar';
import {
  GeneralSection,
  WidgetsSection,
  DynamicWidgetSection,
  ApiEndpointsSection,
  TileConfigSection,
  TagsSection,
  PromptSection,
  ContentRestrictionsSection,
  InputTextSection,
  SlotsInputMediaSection,
  ApiSection,
  EnhanceFieldMappingsSection,
  PipelineStepsSection,
  type WorkflowCategory,
  type WidgetType,
  type WorkflowWidget,
  type WorkflowWithTranslations,
  type FormData,
  type PipelineMode
} from './sections';
import { useEscapeKey } from '../../../../hooks/useEscapeKey';

interface EditWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: () => Promise<void>;
  onShowUrlManager?: () => void; // Keep for backward compatibility but won't be used
  onExportWorkflow?: (workflowId: number) => Promise<void>;
  workflow: WorkflowWithTranslations | null;
  workflowWidgets: WorkflowWidget[];
  widgetTypes: WidgetType[];
  selectedLanguage: Language;
  saving: boolean;
  formData: FormData;
  onFormDataChange: (field: string, value: any) => void;
  onWidgetToggle: (widgetId: number, isEnabled: boolean) => void;
  onDisplayOrderChange: (widgetId: number, order: number) => void;
  selectedCategoryIds: number[];
  onCategoryIdsChange: (ids: number[]) => void;
  selectedTagIds: number[];
  onTagIdsChange: (ids: number[]) => void;
  onWidgetConfigChange?: (widgetTypeId: number, configData: string) => void;
}

export default function EditWorkflowModal({
  isOpen,
  onClose,
  onSave,
  onExportWorkflow,
  workflow,
  workflowWidgets,
  widgetTypes,
  selectedLanguage,
  saving,
  formData,
  onFormDataChange,
  onWidgetToggle,
  onDisplayOrderChange,
  selectedCategoryIds,
  onCategoryIdsChange,
  selectedTagIds,
  onTagIdsChange,
  onWidgetConfigChange
}: EditWorkflowModalProps) {
  useEscapeKey(onClose, isOpen);
  const t = useLocalization(selectedLanguage);
  const [activeSection, setActiveSection] = useState<SectionId>('general');
  const [exporting, setExporting] = useState(false);

  const handleExport = async () => {
    if (!workflow?.id || !onExportWorkflow) return;
    setExporting(true);
    try {
      await onExportWorkflow(workflow.id);
    } finally {
      setExporting(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isOutgoingImagePolicyValid(formData.settings?.outgoing_image_policy)) return;
    await onSave();
  };

  const handleTileMediaUpload = async (file: File, mediaType: string) => {
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    const token = await window.Clerk?.session?.getToken({ template: 'withmail' });
    if (!token) {
      throw new Error('Not authenticated');
    }

    const response = await fetch(
      `/api/admin/workflows/${workflow.id}/upload-tile-media?mediaType=${mediaType}`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`
        },
        body: uploadFormData
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Upload failed');
    }

    const { url } = await response.json();

    const currentConfig = formData.tile_config || {
      displayType: null,
      imageUrl: null,
      imageUrl2: null,
      videoUrl: null,
      comparisonMode: 'slider',
      autoSwitchDelay: 2000,
      slideshowImages: [],
      slideshowDelay: 1000,
      slideshowTransition: 'fade' as 'instant' | 'fade' | 'slide'
    };

    const updatedConfig = { ...currentConfig };
    if (mediaType === 'image') {
      updatedConfig.imageUrl = url;
    } else if (mediaType === 'image2') {
      updatedConfig.imageUrl2 = url;
    } else if (mediaType === 'video') {
      updatedConfig.videoUrl = url;
    } else if (mediaType.startsWith('slideshow_')) {
      const index = parseInt(mediaType.split('_')[1]);
      const currentImages = [...(updatedConfig.slideshowImages || [])];
      currentImages[index] = url;
      updatedConfig.slideshowImages = currentImages;
    }

    onFormDataChange('tile_config', updatedConfig);
  };

  // Calculate section status for indicators
  const sectionStatus = useMemo<Record<SectionId, SectionStatus>>(() => {
    return {
      general: {
        hasErrors: !formData.title || !formData.visual_id,
        isComplete: !!formData.title && !!formData.visual_id && !!formData.pricing_formula
      },
      widgets: {
        hasErrors: false,
        isComplete: workflowWidgets.some(w => w.is_enabled)
      },
      dynamic_widget: {
        hasErrors: false,
        isComplete: !!(formData.dynamic_widget?.name && formData.dynamic_widget?.fields?.length > 0)
      },
      api_endpoints: {
        hasErrors: false, // Will be checked by ApiEndpointsSection itself
        isComplete: true  // URLs are loaded asynchronously
      },
      tile_config: {
        hasErrors: false,
        isComplete: !!formData.tile_config?.displayType
      },
      tags: {
        hasErrors: false,
        isComplete: true // Tags are optional, always complete
      },
      prompt: {
        hasErrors: false,
        isComplete: true // Prompt categories are optional, always complete
      },
      content_restrictions: {
        hasErrors: !isOutgoingImagePolicyValid(formData.settings?.outgoing_image_policy),
        isComplete: isOutgoingImagePolicyValid(formData.settings?.outgoing_image_policy)
          && !!(formData.min_image_width || formData.input_content_config || formData.settings?.outgoing_image_policy?.enabled)
      },
      input_text: {
        hasErrors: false,
        isComplete: workflowWidgets.some(w => w.widget_type?.name === 'Input Text' && w.is_enabled && w.config_data)
      },
      slots_input_media: {
        hasErrors: false,
        isComplete: workflowWidgets.some(w => (w.widget_type?.name === 'Slots Input Media' || w.widget_type?.name === 'Slots Input Media Small') && w.is_enabled && w.config_data)
      },
      pipeline_steps: {
        hasErrors: (formData.settings?.steps ?? []).some(
          s => !s.name || !s.url || !s.api_key
        ),
        isComplete: (formData.settings?.steps?.length ?? 0) > 0
      },
      enhance_mappings: {
        hasErrors: false,
        isComplete: (formData.settings?.enhance_field_mappings?.length ?? 0) > 0
      },
      api: {
        hasErrors: false,
        isComplete: true // API documentation is always available
      }
    };
  }, [formData, workflowWidgets]);

  // Compute hidden sections based on pipeline mode
  const hiddenSections = useMemo<Set<SectionId>>(() => {
    const pipelineMode: PipelineMode = formData.settings?.pipeline_mode
      || (formData.settings?.auto_prompt_enhance ? 'auto_prompt_enhance' : 'off');
    const hidden = new Set<SectionId>();
    switch (pipelineMode) {
      case 'off':
        hidden.add('pipeline_steps');
        hidden.add('enhance_mappings');
        break;
      case 'auto_prompt_enhance':
        hidden.add('pipeline_steps');
        break;
      case 'pipeline':
        hidden.add('api_endpoints');
        hidden.add('enhance_mappings');
        break;
    }
    return hidden;
  }, [formData.settings?.pipeline_mode, formData.settings?.auto_prompt_enhance]);

  // Auto-navigate to general if current section becomes hidden
  useEffect(() => {
    if (hiddenSections.has(activeSection)) {
      setActiveSection('general');
    }
  }, [hiddenSections, activeSection]);

  // Section titles for header
  const sectionTitles: Record<SectionId, string> = {
    general: t.admin?.general || 'General',
    widgets: t.admin?.widgets || 'Widgets',
    dynamic_widget: t.admin?.dynamicWidget || 'Dynamic Widget',
    api_endpoints: t.admin?.apiEndpoints || 'Dify Endpoints',
    tile_config: t.admin?.tileConfig || 'Tile Config',
    tags: t.admin?.tags || 'Tags',
    prompt: t.admin?.references || 'References',
    content_restrictions: t.admin?.restrictions || 'Restrictions',
    input_text: t.admin?.inputTextSettings || 'Input Text',
    slots_input_media: t.admin?.slotsInputMediaSettings || 'Slots Media',
    pipeline_steps: t.admin?.pipelineSteps || 'Pipeline Steps',
    enhance_mappings: t.admin?.enhanceMappings || 'Enhance Mappings',
    api: t.admin?.api || 'API'
  };

  if (!isOpen || !workflow) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <div
        className="bg-gray-900 rounded-2xl border border-gray-700 w-full max-w-7xl h-[90vh] flex overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Sidebar */}
        <WorkflowModalSidebar
          activeSection={activeSection}
          onSectionChange={setActiveSection}
          sectionStatus={sectionStatus}
          language={selectedLanguage}
          workflowTitle={formData.title || workflow.description_key}
          hiddenSections={hiddenSections}
        />

        {/* Main Content Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Header */}
          <header className="px-6 py-4 border-b border-gray-700 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-bold text-white">
                {sectionTitles[activeSection]}
              </h2>
              {sectionStatus[activeSection].hasErrors && (
                <span className="px-2 py-0.5 bg-red-900/50 border border-red-500/50 rounded text-xs text-red-300">
                  {t.admin?.hasErrors || 'Has errors'}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {/* Export button - visible on General section */}
              {activeSection === 'general' && onExportWorkflow && (
                <button
                  type="button"
                  onClick={handleExport}
                  disabled={exporting || saving}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-gray-300 hover:text-white rounded-lg transition-colors text-sm"
                  title={t.adminWorkflows?.exportWorkflow || 'Export Workflow'}
                >
                  {exporting ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-400 border-t-transparent"></div>
                  ) : (
                    <Icon name={ICONS.DOWNLOAD} size={18} />
                  )}
                  <span>{t.adminWorkflows?.exportWorkflow || 'Export'}</span>
                </button>
              )}
              <button
                type="button"
                onClick={onClose}
                disabled={saving}
                className="text-gray-400 hover:text-white transition-colors p-2 rounded-lg hover:bg-gray-800"
              >
                <Icon name={ICONS.X} size={24} />
              </button>
            </div>
          </header>

          {/* Section Content - Scrollable */}
          <form onSubmit={handleSubmit} className="flex-1 flex flex-col overflow-hidden">
            <div className="flex-1 overflow-y-auto p-6">
              {activeSection === 'general' && (
                <GeneralSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                />
              )}

              {activeSection === 'widgets' && (
                <WidgetsSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                  workflowWidgets={workflowWidgets}
                  widgetTypes={widgetTypes}
                  onWidgetToggle={onWidgetToggle}
                  onDisplayOrderChange={onDisplayOrderChange}
                  onNavigateToDynamicWidget={() => setActiveSection('dynamic_widget')}
                />
              )}

              {activeSection === 'dynamic_widget' && (
                <DynamicWidgetSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                />
              )}

              {activeSection === 'api_endpoints' && (
                <ApiEndpointsSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                  workflowId={workflow.id}
                />
              )}

              {activeSection === 'tile_config' && (
                <TileConfigSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                  onTileMediaUpload={handleTileMediaUpload}
                />
              )}

              {activeSection === 'tags' && (
                <TagsSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                  workflowId={workflow.id}
                  selectedTagIds={selectedTagIds}
                  onTagIdsChange={onTagIdsChange}
                />
              )}

              {activeSection === 'prompt' && (
                <PromptSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                  workflowId={workflow.id}
                  selectedCategoryIds={selectedCategoryIds}
                  onCategoryIdsChange={onCategoryIdsChange}
                  maxReferenceImages={formData.input_content_config?.referenceImages?.maxCount ?? null}
                  onMaxReferenceImagesChange={(value) => {
                    const currentConfig = formData.input_content_config || {};
                    onFormDataChange('input_content_config', {
                      ...currentConfig,
                      referenceImages: value !== null ? { maxCount: value } : undefined
                    });
                  }}
                  maxReferenceVideos={formData.input_content_config?.referenceVideos?.maxCount ?? null}
                  onMaxReferenceVideosChange={(value) => {
                    const currentConfig = formData.input_content_config || {};
                    onFormDataChange('input_content_config', {
                      ...currentConfig,
                      referenceVideos: value !== null ? { maxCount: value } : undefined
                    });
                  }}
                  maxReferenceAudio={formData.input_content_config?.referenceAudio?.maxCount ?? null}
                  onMaxReferenceAudioChange={(value) => {
                    const currentConfig = formData.input_content_config || {};
                    onFormDataChange('input_content_config', {
                      ...currentConfig,
                      referenceAudio: value !== null ? { maxCount: value } : undefined
                    });
                  }}
                />
              )}

              {activeSection === 'content_restrictions' && (
                <ContentRestrictionsSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                  workflowWidgets={workflowWidgets}
                />
              )}

              {activeSection === 'input_text' && onWidgetConfigChange && (
                <InputTextSection
                  workflowWidgets={workflowWidgets}
                  onWidgetConfigChange={onWidgetConfigChange}
                  language={selectedLanguage}
                />
              )}

              {activeSection === 'slots_input_media' && onWidgetConfigChange && (
                <SlotsInputMediaSection
                  workflowWidgets={workflowWidgets}
                  onWidgetConfigChange={onWidgetConfigChange}
                  language={selectedLanguage}
                  inputFilesLogic={formData?.input_files_logic}
                />
              )}

              {activeSection === 'pipeline_steps' && (
                <PipelineStepsSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                />
              )}

              {activeSection === 'enhance_mappings' && (
                <EnhanceFieldMappingsSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                />
              )}

              {activeSection === 'api' && (
                <ApiSection
                  formData={formData}
                  onFormDataChange={onFormDataChange}
                  workflow={workflow}
                  language={selectedLanguage}
                  workflowId={workflow.id}
                />
              )}
            </div>

            {/* Footer - Sticky */}
            <footer className="px-6 py-4 border-t border-gray-700 flex items-center justify-between flex-shrink-0 bg-gray-900">
              <div className="flex items-center gap-2 text-sm text-gray-400">
                <Icon name={ICONS.INFO} size={16} />
                <span>{t.admin?.allSectionsWillBeSaved || 'All sections will be saved together'}</span>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={saving}
                  className="px-6 py-2.5 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 text-white rounded-lg font-medium transition-colors"
                >
                  {t.admin?.cancel || 'Cancel'}
                </button>

                <button
                  type="submit"
                  disabled={saving || !isOutgoingImagePolicyValid(formData.settings?.outgoing_image_policy)}
                  className="px-6 py-2.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      {t.admin?.saving || 'Saving...'}
                    </>
                  ) : (
                    <>
                      <Icon name={ICONS.CHECK} size={18} />
                      {t.admin?.saveWorkflow || 'Save Workflow'}
                    </>
                  )}
                </button>
              </div>
            </footer>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Re-export types for backward compatibility
export type { WorkflowCategory } from './sections';
